import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { TextField } from "@mui/material";

import { useAppDispatch, useAppSelector } from "~/common/hooks/useRedux";
import { useAppConfigQuery } from "~/features/User/queries";

import { IHealthSystem } from "@/api";
import { Autocomplete } from "@/common/components";
import { setCurrentHealthSystemId } from "@/common/store";
import { black } from "@/common/theming";

const emptyHealthSystems = [] as IHealthSystem[];

export const HealthSystemPicker = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedHealthSystemQueryParam = searchParams.get("healthSystem");

  const healthSystems = useAppConfigQuery().data?.healthSystems || emptyHealthSystems;
  const dispatch = useAppDispatch();
  const selectedHealthSystemId = useAppSelector((state) => state.common.currentHealthSystemId);

  const filteredHealthSystems = useMemo(() => {
    return [...healthSystems].filter((healthSystem) => !healthSystem.archivedAt);
  }, [healthSystems]);

  const selectedHealthSystemFromQuery = useMemo(
    () =>
      filteredHealthSystems.find(
        (healthSystem) => healthSystem.name === selectedHealthSystemQueryParam,
      ),
    [filteredHealthSystems, selectedHealthSystemQueryParam],
  );
  const selectedHealthSystemFromStore = useMemo(() => {
    return filteredHealthSystems.find((healthSystem) => healthSystem.id === selectedHealthSystemId);
  }, [filteredHealthSystems, selectedHealthSystemId]);

  useEffect(() => {
    if (!selectedHealthSystemFromStore && healthSystems.length >= 1) {
      dispatch(setCurrentHealthSystemId(healthSystems[0]!.id));
    }
  }, [dispatch, healthSystems, selectedHealthSystemFromStore]);

  useEffect(() => {
    if (
      selectedHealthSystemFromQuery &&
      selectedHealthSystemFromQuery !== selectedHealthSystemFromStore
    ) {
      // Set healthsystems in params if there is more than one healthsystem
      if (filteredHealthSystems.length <= 1) return;

      setSearchParams((otherParams) => {
        otherParams.set("healthSystem", selectedHealthSystemFromQuery.name);
        return otherParams;
      });

      dispatch(setCurrentHealthSystemId(selectedHealthSystemFromQuery.id));
    }
  }, [
    dispatch,
    filteredHealthSystems.length,
    selectedHealthSystemFromQuery,
    selectedHealthSystemFromStore,
    setSearchParams,
  ]);

  if (selectedHealthSystemFromStore) {
    const disabled = filteredHealthSystems.length === 1;

    return (
      <Autocomplete
        trackingLabel="Facility Picker"
        sx={{
          width: "200px",
          ".MuiInputBase-root": { padding: "0 10px" },
          ...(disabled
            ? {
                ".MuiAutocomplete-endAdornment": { display: "none" },
                "input.Mui-disabled": { "-webkit-text-fill-color": black },
              }
            : {}),
        }}
        options={filteredHealthSystems}
        getOptionLabel={(option) => option?.name || ""}
        value={selectedHealthSystemFromStore}
        disableClearable={true}
        renderInput={(params) => <TextField {...params} label="Health System" />}
        disabled={disabled}
        renderOption={(props, option) => {
          return <li {...props}>{option.name}</li>;
        }}
        onChange={(_event, healthSystem) => {
          if (healthSystem) {
            dispatch(setCurrentHealthSystemId(healthSystem.id));

            // Set healthsystem in params if there are more than one healthsystem
            if (filteredHealthSystems.length > 1)
              setSearchParams((otherParams) => {
                otherParams.set("healthSystem", healthSystem.name);
                return otherParams;
              });
          }
        }}
      />
    );
  }

  return <></>;
};
